import { useTranslation } from "react-i18next";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { CookiePreferencesModal } from "./CookiePreferencesModal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useComplianceMode, useIsPIPL } from "@/stores/regionStore";

interface CookieConsentBannerProps {
  /** 自定义类名 */
  className?: string;
  /** Privacy Policy 链接 */
  privacyPolicyUrl?: string;
  /** Cookie Policy 链接 */
  cookiePolicyUrl?: string;
}

/**
 * Cookie 同意横幅组件 (区域合规适配版本)
 *
 * 根据用户所在区域的 compliance_mode 显示不同样式的 Cookie 同意横幅：
 * - GDPR (EU): 三按钮布局（拒绝/接受/设置），默认禁用非必要 Cookie
 * - CCPA (US): 显示 "Do Not Sell My Info" 链接
 * - Standard (JP/GLOBAL): 通知型横幅
 * - PIPL (CN): 不显示（仅使用必要 Cookie）
 *
 * 特性：
 * - 从 RegionStore 读取 complianceMode
 * - 根据区域自动切换显示样式
 * - 支持亮色/暗色主题
 * - 完整的 ARIA 可访问性支持
 * - 平滑的显示/隐藏动画
 * - 多语言支持
 *
 * **Validates: Requirements 4.1, 4.2, 4.3, 4.4, 4.5**
 */
export function CookieConsentBanner({
  className,
  privacyPolicyUrl = "/privacy",
  cookiePolicyUrl = "/privacy#cookies",
}: CookieConsentBannerProps) {
  const { t } = useTranslation("common");
  const complianceMode = useComplianceMode();
  const isPIPL = useIsPIPL();
  const {
    shouldShowBanner,
    showPreferencesModal,
    preferences,
    isLoading,
    acceptAll,
    rejectNonEssential,
    savePreferences,
    openPreferencesModal,
    closePreferencesModal,
  } = useCookieConsent();

  // 加载中时返回 null
  if (isLoading) {
    return null;
  }

  // PIPL (中国) 模式不显示 Cookie Banner
  // **Validates: Requirements 4.4**
  if (isPIPL) {
    return null;
  }

  // 根据 compliance_mode 渲染不同的横幅
  const renderBannerContent = () => {
    switch (complianceMode) {
      case "gdpr":
        return renderGDPRBanner();
      case "ccpa":
        return renderCCPABanner();
      case "standard":
      default:
        return renderStandardBanner();
    }
  };

  /**
   * GDPR (欧盟) 模式 - 完整的三按钮布局
   * **Validates: Requirements 4.1**
   */
  const renderGDPRBanner = () => (
    <div className="p-4 sm:p-5">
      {/* 标题 */}
      <h2 className="text-sm font-semibold text-black dark:text-white mb-2">
        {t("cookieConsent.banner.title")}
      </h2>

      {/* 描述文本 */}
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
        {t("cookieConsent.gdpr.description", {
          defaultValue: t("cookieConsent.banner.description"),
        })}
      </p>

      {/* 按钮组 - GDPR 三按钮布局 */}
      <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-3">
        {/* Reject Non-Essential - 拒绝按钮 */}
        <Button
          onClick={rejectNonEssential}
          variant="outline"
          className="w-full sm:w-auto border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900 text-black dark:text-white text-xs h-8 px-3 rounded-md font-medium transition-colors"
          aria-label={t("cookieConsent.gdpr.reject", {
            defaultValue: t("cookieConsent.banner.rejectNonEssential"),
          })}
        >
          {t("cookieConsent.gdpr.reject", {
            defaultValue: t("cookieConsent.banner.rejectNonEssential"),
          })}
        </Button>

        {/* Accept All - 接受按钮 */}
        <Button
          onClick={acceptAll}
          className="w-full sm:w-auto bg-black hover:bg-gray-800 text-white dark:bg-white dark:hover:bg-gray-100 dark:text-black text-xs h-8 px-3 rounded-md font-medium transition-colors"
          aria-label={t("cookieConsent.gdpr.accept", {
            defaultValue: t("cookieConsent.banner.acceptAll"),
          })}
        >
          {t("cookieConsent.gdpr.accept", {
            defaultValue: t("cookieConsent.banner.acceptAll"),
          })}
        </Button>

        {/* Cookie Settings - 设置按钮 */}
        <Button
          onClick={openPreferencesModal}
          variant="outline"
          className="w-full sm:w-auto border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-900 text-black dark:text-white text-xs h-8 px-3 rounded-md font-medium transition-colors"
          aria-label={t("cookieConsent.gdpr.settings", {
            defaultValue: t("cookieConsent.banner.cookieSettings"),
          })}
        >
          {t("cookieConsent.gdpr.settings", {
            defaultValue: t("cookieConsent.banner.cookieSettings"),
          })}
        </Button>
      </div>

      {/* 底部链接行 */}
      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          <a
            href={privacyPolicyUrl}
            className="hover:text-black dark:hover:text-white transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("cookieConsent.banner.privacyPolicy")}
          </a>
          <span>·</span>
          <a
            href={cookiePolicyUrl}
            className="hover:text-black dark:hover:text-white transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("cookieConsent.banner.cookiePolicy")}
          </a>
        </div>
      </div>
    </div>
  );

  /**
   * CCPA (美国) 模式 - 带 "Do Not Sell My Info" 链接
   * **Validates: Requirements 4.2**
   */
  const renderCCPABanner = () => (
    <div className="p-4 sm:p-5">
      {/* 描述文本 */}
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
        {t("cookieConsent.ccpa.description", {
          defaultValue:
            "We use cookies to enhance your experience. You have the right to opt-out of the sale of your personal information.",
        })}
      </p>

      {/* 按钮和链接行 */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Do Not Sell My Info 链接 */}
          <a
            href={`${privacyPolicyUrl}#do-not-sell`}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
            aria-label={t("cookieConsent.ccpa.doNotSell", {
              defaultValue: "Do Not Sell My Personal Information",
            })}
          >
            {t("cookieConsent.ccpa.doNotSell", {
              defaultValue: "Do Not Sell My Personal Information",
            })}
          </a>
        </div>

        {/* Got It 按钮 */}
        <Button
          onClick={acceptAll}
          className="w-full sm:w-auto bg-black hover:bg-gray-800 text-white dark:bg-white dark:hover:bg-gray-100 dark:text-black text-xs h-8 px-4 rounded-md font-medium transition-colors"
          aria-label={t("cookieConsent.ccpa.gotIt", { defaultValue: "Got It" })}
        >
          {t("cookieConsent.ccpa.gotIt", { defaultValue: "Got It" })}
        </Button>
      </div>
    </div>
  );

  /**
   * Standard (日本/全球) 模式 - 通知型横幅
   * **Validates: Requirements 4.3**
   */
  const renderStandardBanner = () => (
    <div className="p-4 sm:p-5">
      {/* 描述文本 */}
      <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
        {t("cookieConsent.standard.description", {
          defaultValue:
            "This website uses cookies to ensure you get the best experience on our website.",
        })}
      </p>

      {/* 按钮行 */}
      <div className="flex items-center justify-between">
        {/* 政策链接 */}
        <a
          href={privacyPolicyUrl}
          className="text-xs text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("cookieConsent.banner.privacyPolicy")}
        </a>

        {/* OK 按钮 */}
        <Button
          onClick={acceptAll}
          className="bg-black hover:bg-gray-800 text-white dark:bg-white dark:hover:bg-gray-100 dark:text-black text-xs h-8 px-4 rounded-md font-medium transition-colors"
          aria-label={t("cookieConsent.standard.ok", { defaultValue: "OK" })}
        >
          {t("cookieConsent.standard.ok", { defaultValue: "OK" })}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Cookie 同意横幅 - 根据 compliance_mode 变形 */}
      {shouldShowBanner && (
        <div
          role="region"
          aria-label={t("cookieConsent.banner.title")}
          data-compliance-mode={complianceMode}
          className={cn(
            // 固定底部定位
            "fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-md z-50",
            // Vercel 风格：纯白/纯黑背景，清晰边框
            "bg-white dark:bg-black",
            "border border-gray-200 dark:border-gray-800",
            "rounded-lg",
            // 阴影
            "shadow-xl dark:shadow-2xl dark:shadow-gray-900/50",
            // 动画
            "animate-in slide-in-from-bottom duration-300",
            className
          )}
        >
          {renderBannerContent()}
        </div>
      )}

      {/* Cookie 偏好设置弹窗 - 仅 GDPR 模式需要 */}
      <CookiePreferencesModal
        open={showPreferencesModal}
        onClose={closePreferencesModal}
        preferences={preferences}
        onSave={savePreferences}
      />
    </>
  );
}

export default CookieConsentBanner;
